import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { FORM_ERROR_MAPPINGS } from './error-mappings';

export type FormStatusState = 'idle' | 'saving' | 'saved' | 'error';

export interface FormRegistration {
    id: string;
    state: FormStatusState;
    errors: string[];
}

@Injectable({
    providedIn: 'root',
})
export class CentralFormRegistryService {
    // Map to hold the state of every form on the screen
    private formsMap = signal<Map<string, FormRegistration>>(new Map());

    private globalSaveSource = new Subject<void>();
    globalSave$ = this.globalSaveSource.asObservable();

    triggerGlobalSave() {
        console.log("here")
        this.globalSaveSource.next();
    }

    /**
     * Registers or updates a form's state in the centralized registry
     */
    updateFormState(id: string, state: FormStatusState, rawErrors: string[] = []) {
        const cleanErrors = mapRawErrors(rawErrors);
        this.formsMap.update((map) => {
            const newMap = new Map(map);
            newMap.set(id, { id, state, errors: cleanErrors });
            return newMap;
        });
    }

    /**
     * Unregisters a form when its component is destroyed
     */
    unregisterForm(id: string) {
        this.formsMap.update((map) => {
            const newMap = new Map(map);
            newMap.delete(id);
            return newMap;
        });
    }

    /**
     * Derived Signal: Computes the absolute master state across all active forms
     */
    masterState = computed<FormStatusState>(() => {
        const forms = Array.from(this.formsMap().values());

        if (forms.length === 0) return 'idle';
        if (forms.some((f) => f.state === 'error')) return 'error';
        if (forms.some((f) => f.state === 'saving')) return 'saving';
        if (forms.some((f) => f.state === 'saved')) return 'saved';

        return 'idle';
    });

    /**
     * Derived Signal: Combines all validation and network errors from all forms
     */
    masterErrors = computed<string[]>(() => {
        return Array.from(this.formsMap().values())
            .flatMap((f) => f.errors);
    });
}

// form-error.utils.ts
export function mapRawErrors(rawErrors: string[]): string[] {
    return rawErrors.map(error => {
        // Find a matching constraint key inside the raw error string
        const matchedKey = Object.keys(FORM_ERROR_MAPPINGS).find(key =>
            error.includes(key)
        );

        // Return the clean message if found, otherwise clean up the Postgres prefix
        if (matchedKey) {
            return FORM_ERROR_MAPPINGS[matchedKey];
        }

        // Optional fallback: Strip out the "[survey_element]" prefix to make unmapped errors look slightly better
        return error.replace(/^\[.*?\]\s*/, '');
    });
}